import { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Heading, Input, Textarea, Stack, Text, useToast,
  Flex, Spacer, HStack, Collapse, Badge, FormLabel, FormControl,
  IconButton, VStack, Container, Select, SimpleGrid, Divider,
} from '@chakra-ui/react';
import axios from 'axios';
import { auth, googleProvider } from './firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { DeleteIcon, DownloadIcon, CheckIcon, EditIcon, CopyIcon } from '@chakra-ui/icons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let loginInProgress = false;

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  SAVED:     { label: 'Saved',     bg: 'gray.100',   color: 'gray.600'   },
  APPLIED:   { label: 'Applied',   bg: 'blue.50',    color: 'blue.600'   },
  INTERVIEW: { label: 'Interview', bg: 'purple.50',  color: 'purple.600' },
  OFFER:     { label: 'Offer',     bg: 'green.50',   color: 'green.600'  },
  REJECTED:  { label: 'Rejected',  bg: 'red.50',     color: 'red.500'    },
};

const App = () => {
  const [user, setUser] = useState<any>(null);
  const [uses, setUses] = useState<number | null>(null);
  const [hasPaid, setHasPaid] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [letters, setLetters] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [jobPosting, setJobPosting] = useState('');
  const [cv, setCv] = useState<File | null>(null);
  const [tone, setTone] = useState('formal');
  const [length, setLength] = useState('standard');

  const [expandedLetterId, setExpandedLetterId] = useState<string | null>(null);
  const [editingLetterId, setEditingLetterId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [showNotes, setShowNotes] = useState<Record<string, boolean>>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortOrder, setSortOrder] = useState('newest');

  const toast = useToast();

  const fetchUserData = async (currentUser: any) => {
    const token = await currentUser.getIdToken();
    const [meRes, lettersRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/me`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${BACKEND_URL}/letters`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setUses(meRes.data.uses);
    setHasPaid(meRes.data.hasPaid);
    setHasResume(meRes.data.hasResume);
    setLetters(lettersRes.data);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUses(null);
    setHasPaid(false);
    setHasResume(false);
    setLetters([]);
    toast({ title: 'Logged out', status: 'info', duration: 3000, isClosable: true });
  };

  const login = async () => {
    if (loginInProgress) return;
    loginInProgress = true;
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      await fetchUserData(result.user);
    } catch (err: any) {
      toast({ title: 'Login failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      loginInProgress = false;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchUserData(currentUser);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      toast({ title: 'Please log in first', status: 'info', duration: 3000, isClosable: true });
      return;
    }
    if (!cv && !hasResume) {
      toast({ title: 'Please upload your CV', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (uses !== null && uses >= 3 && !hasPaid) {
      toast({ title: 'Limit reached', description: 'Upgrade to continue generating letters', status: 'warning', duration: 5000, isClosable: true });
      return;
    }

    setIsLoading(true);
    const token = await user.getIdToken();
    const formData = new FormData();
    formData.append('jobTitle', jobTitle);
    formData.append('company', company);
    formData.append('location', location);
    formData.append('jobPosting', jobPosting);
    formData.append('tone', tone);
    formData.append('length', length);
    if (cv) formData.append('cv', cv);

    try {
      const res = await axios.post(`${BACKEND_URL}/generate`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLetters((prev) => [res.data, ...prev]);
      setUses((prev) => (prev ?? 0) + 1);
      if (cv) setHasResume(true);
      toast({ title: 'Cover Letter Generated', status: 'success', duration: 3000, isClosable: true });
    } catch (err: any) {
      toast({ title: 'Generation Failed', description: err.response?.data?.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const updateLetter = async (letterId: string, data: Record<string, any>) => {
    const token = await user.getIdToken();
    const res = await axios.patch(`${BACKEND_URL}/letters/${letterId}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setLetters((prev) => prev.map((l) => (l.id === letterId ? { ...l, ...res.data } : l)));
  };

  const saveEdit = async (letterId: string) => {
    await updateLetter(letterId, { coverLetter: editingText });
    setEditingLetterId(null);
    toast({ title: 'Letter saved', status: 'success', duration: 2000, isClosable: true });
  };

  const saveNotes = async (letterId: string) => {
    await updateLetter(letterId, { notes: editingNotes[letterId] ?? '' });
    toast({ title: 'Notes saved', status: 'success', duration: 2000, isClosable: true });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard', status: 'success', duration: 2000, isClosable: true });
  };

  const downloadPdf = async (letterId: string) => {
    const token = await user.getIdToken();
    const res = await axios.get(`${BACKEND_URL}/letters/${letterId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cover_letter_${letterId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const removeLetter = async (letterId: string) => {
    const token = await user.getIdToken();
    await axios.delete(`${BACKEND_URL}/letters/${letterId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setLetters((prev) => prev.filter((l) => l.id !== letterId));
    toast({ title: 'Letter removed', status: 'info', duration: 3000, isClosable: true });
  };

  const handleUpgrade = async () => {
    if (!user) {
      toast({ title: 'Please log in first', status: 'info', duration: 3000, isClosable: true });
      return;
    }
    const token = await user.getIdToken();
    try {
      const res = await axios.post(`${BACKEND_URL}/stripe/checkout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch {
      toast({ title: 'Error redirecting to Stripe', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const stats = useMemo(() => ({
    total: letters.length,
    applied: letters.filter((l) => ['APPLIED', 'INTERVIEW', 'OFFER'].includes(l.status)).length,
    interviews: letters.filter((l) => l.status === 'INTERVIEW').length,
    offers: letters.filter((l) => l.status === 'OFFER').length,
  }), [letters]);

  const filteredLetters = useMemo(() => {
    let result = [...letters];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((l) =>
        l.company.toLowerCase().includes(q) || l.jobTitle.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'ALL') {
      result = result.filter((l) => l.status === statusFilter);
    }
    if (sortOrder === 'oldest') result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    else if (sortOrder === 'company') result.sort((a, b) => a.company.localeCompare(b.company));
    else result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return result;
  }, [letters, searchQuery, statusFilter, sortOrder]);

  return (
    <Box minH="100vh" bg="#F7F8FC">

      {/* Header */}
      <Box position="sticky" top="0" zIndex="10" bg="white" borderBottom="1px solid" borderColor="gray.100" px={6} py={4}>
        <Container maxW="860px" p={0}>
          <Flex align="center">
            <HStack spacing={3}>
              <Box w={8} h={8} bgGradient="linear(to-br, indigo.500, purple.500)" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                <Text color="white" fontSize="11px" fontWeight="800" letterSpacing="-0.02em">CL</Text>
              </Box>
              <Heading size="md" fontWeight="700" letterSpacing="-0.02em" color="gray.900">
                CoverLetter<Text as="span" color="indigo.500">AI</Text>
              </Heading>
            </HStack>
            <Spacer />
            <HStack spacing={2}>
              {user && (
                <Button size="sm" variant="ghost" color="gray.500" fontWeight="500" _hover={{ bg: 'gray.100', color: 'gray.700' }} onClick={logout}>
                  Log out
                </Button>
              )}
              {!user && (
                <Button size="sm" bg="gray.900" color="white" fontWeight="600" borderRadius="full" px={5} _hover={{ bg: 'gray.700' }} onClick={login}>
                  Sign in with Google
                </Button>
              )}
              {uses !== null && uses >= 3 && !hasPaid && (
                <Button size="sm" bgGradient="linear(to-r, indigo.500, purple.500)" color="white" fontWeight="600" borderRadius="full" px={5} _hover={{ bgGradient: 'linear(to-r, indigo.600, purple.600)', transform: 'translateY(-1px)' }} transition="all 0.15s" onClick={handleUpgrade}>
                  Upgrade — $4.99
                </Button>
              )}
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container maxW="860px" py={12} px={4}>

        {/* Hero */}
        <VStack spacing={3} mb={10} textAlign="center">
          <Heading as="h1" size="2xl" fontWeight="800" letterSpacing="-0.04em" color="gray.900" lineHeight="1.15">
            Free AI Cover Letter Generator
          </Heading>
          <Text color="gray.400" fontSize="lg" fontWeight="400" maxW="460px">
            Upload your resume, paste the job description, and get a tailored cover letter in seconds.
          </Text>
        </VStack>

        {/* Form Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="0 1px 4px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.04)" p={8} mb={12}>
          <Stack spacing={5}>
            <HStack spacing={4} align="flex-start">
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Job Title</FormLabel>
                <Input placeholder="e.g. Software Engineer" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} borderColor="gray.200" borderRadius="lg" _hover={{ borderColor: 'gray.300' }} _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Company</FormLabel>
                <Input placeholder="e.g. Acme Corp" value={company} onChange={(e) => setCompany(e.target.value)} borderColor="gray.200" borderRadius="lg" _hover={{ borderColor: 'gray.300' }} _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Location</FormLabel>
              <Input placeholder="e.g. New York, NY (or Remote)" value={location} onChange={(e) => setLocation(e.target.value)} borderColor="gray.200" borderRadius="lg" _hover={{ borderColor: 'gray.300' }} _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Job Description</FormLabel>
              <Textarea placeholder="Paste the full job posting here..." value={jobPosting} onChange={(e) => setJobPosting(e.target.value)} rows={6} borderColor="gray.200" borderRadius="lg" resize="vertical" _hover={{ borderColor: 'gray.300' }} _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
            </FormControl>

            {/* Tone + Length */}
            <HStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Tone</FormLabel>
                <Select value={tone} onChange={(e) => setTone(e.target.value)} borderColor="gray.200" borderRadius="lg" _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }}>
                  <option value="formal">Formal</option>
                  <option value="conversational">Conversational</option>
                  <option value="enthusiastic">Enthusiastic</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>Length</FormLabel>
                <Select value={length} onChange={(e) => setLength(e.target.value)} borderColor="gray.200" borderRadius="lg" _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }}>
                  <option value="short">Short (~150 words)</option>
                  <option value="standard">Standard (~300 words)</option>
                  <option value="detailed">Detailed (~450 words)</option>
                </Select>
              </FormControl>
            </HStack>

            {/* CV Upload */}
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" letterSpacing="0.06em" textTransform="uppercase" mb={1.5}>
                Your CV{' '}
                <Text as="span" textTransform="none" color="gray.400" fontWeight="400" letterSpacing="0">(PDF)</Text>
              </FormLabel>
              {hasResume && !cv && (
                <Box mb={2} px={3} py={2} bg="green.50" borderRadius="lg" display="inline-flex" alignItems="center" gap={2}>
                  <CheckIcon boxSize={3} color="green.500" />
                  <Text fontSize="sm" color="green.600" fontWeight="500">Using saved resume</Text>
                </Box>
              )}
              <Input type="file" accept=".pdf" onChange={(e) => setCv(e.target.files?.[0] || null)} borderColor="gray.200" borderRadius="lg" pt={1.5} cursor="pointer" _hover={{ borderColor: 'gray.300' }}
                sx={{ '::file-selector-button': { height: '100%', padding: '0 14px', marginRight: '12px', background: '#F3F4F6', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#374151', letterSpacing: '0.02em' } }}
              />
              {hasResume && (
                <Text fontSize="xs" color="gray.400" mt={1}>Upload a new PDF to replace your saved resume.</Text>
              )}
            </FormControl>

            <Flex align="center" justify="space-between" pt={1}>
              {!hasPaid ? (
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.400">{uses ?? '–'} / 3 free uses</Text>
                  {uses !== null && uses >= 3 && (
                    <Badge bg="orange.50" color="orange.500" borderRadius="full" px={2.5} py={0.5} fontSize="xs" fontWeight="600">Limit reached</Badge>
                  )}
                </HStack>
              ) : <Box />}
              <Button bgGradient="linear(to-r, indigo.500, purple.500)" color="white" fontWeight="600" borderRadius="lg" px={8} py={6} fontSize="sm" _hover={{ bgGradient: 'linear(to-r, indigo.600, purple.600)', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(99,102,241,0.35)' }} _active={{ transform: 'translateY(0)', boxShadow: 'none' }} transition="all 0.15s" onClick={handleSubmit} isLoading={isLoading} loadingText="Generating...">
                Generate Cover Letter
              </Button>
            </Flex>
          </Stack>
        </Box>

        {/* Stats */}
        {letters.length > 0 && (
          <SimpleGrid columns={4} spacing={4} mb={8}>
            {[
              { label: 'Total', value: stats.total, color: 'gray.700' },
              { label: 'Applied', value: stats.applied, color: 'blue.600' },
              { label: 'Interviews', value: stats.interviews, color: 'purple.600' },
              { label: 'Offers', value: stats.offers, color: 'green.600' },
            ].map(({ label, value, color }) => (
              <Box key={label} bg="white" borderRadius="xl" p={4} boxShadow="0 1px 3px rgba(0,0,0,0.06)">
                <Text fontSize="2xl" fontWeight="800" color={color}>{value}</Text>
                <Text fontSize="xs" color="gray.400" fontWeight="600" textTransform="uppercase" letterSpacing="0.06em">{label}</Text>
              </Box>
            ))}
          </SimpleGrid>
        )}

        {/* Saved Letters */}
        {letters.length > 0 && (
          <>
            <Flex align="center" mb={4} gap={3} flexWrap="wrap">
              <Heading size="sm" fontWeight="700" letterSpacing="-0.01em" color="gray.700" mr="auto">
                Your Cover Letters
              </Heading>
              <Input placeholder="Search company or role..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} size="sm" w="180px" borderRadius="lg" borderColor="gray.200" _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
              <Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} w="140px" borderRadius="lg" borderColor="gray.200">
                <option value="ALL">All statuses</option>
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
              <Select size="sm" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} w="130px" borderRadius="lg" borderColor="gray.200">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="company">By company</option>
              </Select>
            </Flex>

            <Stack spacing={3}>
              {filteredLetters.map((letter) => {
                const statusCfg = STATUS_CONFIG[letter.status] ?? STATUS_CONFIG.SAVED;
                const isExpanded = expandedLetterId === letter.id;
                const isEditing = editingLetterId === letter.id;
                const noteShown = showNotes[letter.id];

                return (
                  <Box key={letter.id} bg="white" borderRadius="xl" boxShadow="0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)" overflow="hidden" transition="box-shadow 0.2s, transform 0.2s" _hover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.09)', transform: 'translateY(-1px)' }}>
                    <Box p={5}>
                      <Flex justify="space-between" align="flex-start">
                        <Box flex={1} mr={4}>
                          <HStack spacing={2} mb={0.5} align="center">
                            <Heading size="sm" fontWeight="700" color="gray.900">{letter.jobTitle}</Heading>
                            <Badge bg={statusCfg.bg} color={statusCfg.color} borderRadius="full" px={2} py={0.5} fontSize="10px" fontWeight="600">{statusCfg.label}</Badge>
                          </HStack>
                          <HStack spacing={1.5}>
                            <Text fontSize="sm" color="gray.500" fontWeight="500">{letter.company}</Text>
                            {letter.location && (
                              <>
                                <Text color="gray.300" fontSize="sm">·</Text>
                                <Text fontSize="sm" color="gray.400">{letter.location}</Text>
                              </>
                            )}
                          </HStack>
                        </Box>
                        <HStack spacing={1} flexShrink={0}>
                          {/* Status selector */}
                          <Select size="xs" value={letter.status} borderRadius="lg" borderColor="gray.200" w="110px"
                            onChange={(e) => updateLetter(letter.id, { status: e.target.value, applied: ['APPLIED', 'INTERVIEW', 'OFFER'].includes(e.target.value) })}>
                            {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </Select>
                          <IconButton aria-label="Copy letter" icon={<CopyIcon boxSize={3} />} size="sm" variant="ghost" color="gray.400" borderRadius="lg" _hover={{ bg: 'gray.50', color: 'gray.600' }} onClick={() => copyToClipboard(letter.coverLetter)} />
                          <Button size="sm" variant="ghost" color="gray.500" leftIcon={<DownloadIcon boxSize={3} />} onClick={() => downloadPdf(letter.id)} borderRadius="lg" fontSize="xs" fontWeight="600" _hover={{ bg: 'gray.50', color: 'gray.700' }}>PDF</Button>
                          <IconButton aria-label="Delete letter" icon={<DeleteIcon boxSize={3} />} size="sm" variant="ghost" color="gray.300" borderRadius="lg" _hover={{ bg: 'red.50', color: 'red.400' }} onClick={() => removeLetter(letter.id)} />
                        </HStack>
                      </Flex>
                    </Box>

                    {/* Notes */}
                    <Box px={5} pb={noteShown ? 3 : 0}>
                      <Button size="xs" variant="ghost" color="gray.400" fontWeight="500" borderRadius="md" _hover={{ color: 'gray.600', bg: 'gray.50' }}
                        onClick={() => {
                          setShowNotes((prev) => ({ ...prev, [letter.id]: !prev[letter.id] }));
                          if (!editingNotes[letter.id]) setEditingNotes((prev) => ({ ...prev, [letter.id]: letter.notes ?? '' }));
                        }}>
                        {noteShown ? '↑ Hide notes' : '✎ Notes'}
                      </Button>
                      <Collapse in={noteShown}>
                        <Box mt={2}>
                          <Textarea size="sm" placeholder="Add notes (interviewer, salary, follow-up date...)" value={editingNotes[letter.id] ?? letter.notes ?? ''} onChange={(e) => setEditingNotes((prev) => ({ ...prev, [letter.id]: e.target.value }))} rows={3} borderColor="gray.200" borderRadius="lg" fontSize="sm" resize="vertical" _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
                          <Button size="xs" mt={1.5} colorScheme="indigo" variant="solid" bg="indigo.500" color="white" _hover={{ bg: 'indigo.600' }} onClick={() => saveNotes(letter.id)}>Save notes</Button>
                        </Box>
                      </Collapse>
                    </Box>

                    {/* Letter preview / edit */}
                    <Box px={5} pb={4}>
                      <Button size="xs" variant="ghost" color="indigo.400" fontWeight="500" borderRadius="md" _hover={{ color: 'indigo.600', bg: 'indigo.50' }}
                        onClick={() => {
                          if (!isExpanded) {
                            setExpandedLetterId(letter.id);
                            setEditingLetterId(null);
                          } else {
                            setExpandedLetterId(null);
                            setEditingLetterId(null);
                          }
                        }}>
                        {isExpanded ? '↑ Collapse' : '↓ Preview letter'}
                      </Button>

                      <Collapse in={isExpanded}>
                        <Divider my={3} borderColor="gray.100" />
                        {isEditing ? (
                          <Box>
                            <Textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={14} fontSize="sm" borderColor="gray.200" borderRadius="lg" resize="vertical" _focus={{ borderColor: 'indigo.400', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' }} />
                            <HStack mt={2} spacing={2}>
                              <Button size="sm" bg="indigo.500" color="white" _hover={{ bg: 'indigo.600' }} onClick={() => saveEdit(letter.id)}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingLetterId(null)}>Cancel</Button>
                            </HStack>
                          </Box>
                        ) : (
                          <Box>
                            <Text whiteSpace="pre-wrap" fontSize="sm" color="gray.600" lineHeight="1.75">{letter.coverLetter}</Text>
                            <Button size="xs" mt={3} variant="ghost" color="gray.400" leftIcon={<EditIcon boxSize={3} />} borderRadius="md" _hover={{ color: 'gray.600', bg: 'gray.50' }}
                              onClick={() => { setEditingLetterId(letter.id); setEditingText(letter.coverLetter); }}>
                              Edit letter
                            </Button>
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  </Box>
                );
              })}

              {filteredLetters.length === 0 && (
                <Box textAlign="center" py={10}>
                  <Text color="gray.300" fontSize="md" fontWeight="500">No letters match your filters.</Text>
                </Box>
              )}
            </Stack>
          </>
        )}

        {letters.length === 0 && user && (
          <Box textAlign="center" py={16}>
            <Text color="gray.300" fontSize="md" fontWeight="500">No cover letters yet. Generate your first one above.</Text>
          </Box>
        )}

      </Container>
    </Box>
  );
};

export default App;
